import "@assets/css/bootstrap-grid.css";

import { Button } from "@components/ui/button";
import { Calendar } from "@components/ui/calendar";
import { Input } from "@components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@ui/external/components/checkbox";
import PrefixInput from "@ui/external/components/prefixinput";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { DndUpload } from "@ui/internal/components/formelement";
import { ErrorLabel, FormLabel } from "@ui/internal/components/label";
import { getInputField } from "@utils/function/index.function";
import {
  isEditorContentEmpty,
  removeHttpPrefix,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  CareerFormSchema,
  CareerFormTypes,
} from "@utils/interface/form.schema";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

const CareerForm: React.FC = () => {
  const { searchParams } = useHandleAction({});
  const pathname = usePathname();
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<CareerFormTypes>({
    mode: "onChange",
    resolver: zodResolver(CareerFormSchema),
    defaultValues: {
      title: pageMode === "add" ? (searchParams.get("search") as string) : "",
      num_of_vacancy: "0",
    },
  });

  const { data: fetchData, isPending: fetchPending } = useListData<any>({
    endpoint: endpoint["career"],
    key: `${pathname}-${pageItem}`,
    enabled: pageItem ? true : false,
    id: pageItem || "",
    window: false,
  });
  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();
  /* set default value */
  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;

      if (firstResult) {
        if (searchParams.get("page") == "add") {
          return reset({});
        } else {
          console.log("Descruiption Valuie", firstResult);
          reset(() => ({
            ...firstResult,
            apply_link: removeHttpPrefix(firstResult?.apply_link),
            description: firstResult?.description,
            experience_level: [
              {
                label: firstResult?.experience_level?.level_name,
                value: firstResult?.experience_level?.id,
              },
            ],
            num_of_vacancy: String(firstResult?.num_of_vacancy),
            header_code: firstResult?.header_code || "",
            footer_code: firstResult?.footer_code || "",
            meta_title: isEditorContentEmpty(firstResult?.meta_title)
              ? ""
              : firstResult?.meta_title,
            meta_description: isEditorContentEmpty(
              firstResult?.meta_description
            )
              ? ""
              : firstResult?.meta_description,
          }));
        }
      }
    }
  }, [fetchData?.data, reset]);

  const queryClient = useQueryClient();
  const { handleAction } = useHandleAction({});

  const handleMutate = (
    data: CareerFormTypes & { experience_level: any; id: any }
  ) => {
    if (data.enable_auto_expiration && !data.expiration_date) {
      setError("expiration_date", {
        type: "manual",
        message: "Expiration date is required when auto expiration is enabled",
      });
      return;
    }
    if (data.expiration_date && data.expiration_date < new Date()) {
      setError("expiration_date", {
        type: "manual",
        message: "Expiration date should be in the future or today",
      });
      return;
    }
    clearErrors("expiration_date");
    setIsFormPending(true);

    data = {
      ...data,
      apply_link: "https://" + removeHttpPrefix(data?.apply_link),
      ...(data.expiration_date && {
        expiration_date:
          new Date(new Date(data.expiration_date).setHours(23, 59, 59, 999))
            .toISOString()
            .split(".")[0] + "Z",
      }),
      experience_level: data.experience_level.map((item: any) => item.value)[0],
    };
    if (pageItem) {
      data = {
        ...data,
        apply_link: "https://" + removeHttpPrefix(data?.apply_link),
        ...(data.expiration_date && {
          expiration_date:
            new Date(new Date(data.expiration_date).setHours(23, 59, 59, 999))
              .toISOString()
              .split(".")[0] + "Z",
        }),
        id: pageItem,
      };
      // if (!(data.image instanceof File || data.image instanceof Blob))
      //   //@ts-ignore
      //   delete data.image;
    }
    return {
      data,
      endpoint: endpoint["career"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      showToast({
        description: `Careers has been  ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "page-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const { mutate, isPending } = usePostData();
  console.log("errors", errors);
  const onSubmit = (data: CareerFormTypes) => {
    //Add Mutation Data
    const modifiedData = {
      ...data,
      num_of_vacancy: Number(data.num_of_vacancy),
    };

    mutate(handleMutate(modifiedData as any), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  console.log("Watch", watch("image"));
  const numOfVacancy = watch("num_of_vacancy");
  const image = watch("image");

  useEffect(() => {
    if (numOfVacancy !== undefined) {
      clearErrors("num_of_vacancy");
    }
    if (image !== undefined) {
      clearErrors("image");
    }
  }, [numOfVacancy, image]);
  const [_, setDate] = useState(new Date());

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "title",
        label: "Job Title",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },

      {
        name: "experience_level",
        label: "Experience Level",
        colSpan: 6,
        type: "async",
        endpoint: endpoint["experience-level"],
        placeholder: "Select Experience Level",
        format: {
          key: "level_name",
          value: "id",
          mutate: "level_name",
        },
        settings: {
          add: true,
          update: true,
          remove: true,
          debounce: 1000,
        },
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "description",
        label: "Description",
        colSpan: 12,
        type: "editor",
        placeholder: "Description  here",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px]   min-w-full w-full`,
        visable: true,
      },

      {
        name: "",
        type: "component",
        className: "mb-[20px]",
        component: (
          <div className="grid max-md:grid-cols-1 grid-cols-2 gap-4">
            <Controller
              name={"image"}
              control={control}
              render={({ field: { onChange, ...restField } }) => (
                <div className="my-[20px]">
                  {<FormLabel label="Upload Image" />}

                  <DndUpload
                    type="image"
                    cropperHeight={200}
                    cropperWidth={200}
                    {...restField}
                    error={errors["image"] && errors["image"].message}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors("image");
                    }}
                  />
                  {
                    <ErrorLabel
                      message={errors["image"] && errors["image"].message}
                    />
                  }
                </div>
              )}
            />
            <div>
              <div className="flex flex-col my-2 justify-between">
                {<FormLabel label="Enable Auto Expiration" />}
                <div className="flex gap-2 my-3 justify-between items-center">
                  <Controller
                    control={control}
                    name="enable_auto_expiration"
                    render={({ field }) => (
                      <>
                        <div className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            aria-invalid={
                              errors["enable_auto_expiration"] &&
                              errors["enable_auto_expiration"].message
                                ? true
                                : false
                            }
                            checked={field.value === true}
                            onCheckedChange={(value: boolean) => {
                              field.onChange(value);
                              clearErrors("enable_auto_expiration");
                            }}
                            label="Yes"
                          />
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            aria-invalid={
                              errors["enable_auto_expiration"] &&
                              errors["enable_auto_expiration"].message
                                ? true
                                : false
                            }
                            checked={field.value === false}
                            onCheckedChange={(value: boolean) => {
                              field.onChange(!value);
                              clearErrors("enable_auto_expiration");
                            }}
                            label="No"
                          />
                        </div>
                      </>
                    )}
                  />
                </div>
                <ErrorLabel
                  message={
                    errors.enable_auto_expiration &&
                    errors.enable_auto_expiration.message
                  }
                />
              </div>
              {watch("enable_auto_expiration") && (
                <Controller
                  name={"expiration_date"}
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <div className="my-[20px]">
                      <FormLabel label="Expiration Date" />
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={
                              errors["expiration_date"]?.message
                                ? "destructiveoutline"
                                : "outline"
                            }
                            className="w-full"
                          >
                            {value
                              ? new Date(value).toDateString()
                              : "Select Date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-2">
                          <Calendar
                            {...restField}
                            mode="single"
                            showOutsideDays={false}
                            captionLayout="dropdown"
                            selected={
                              value === null ? undefined : (value as Date)
                            }
                            onSelect={(selectedDate) => {
                              if (!selectedDate) {
                                return;
                              }

                              setDate(selectedDate);
                              onChange(selectedDate);

                              const today = new Date();
                              today.setHours(0, 0, 0, 0); // Normalize to start of day

                              selectedDate.setHours(0, 0, 0, 0); // Normalize selected date

                              if (selectedDate.getTime() <= today.getTime()) {
                                return setError("expiration_date", {
                                  message:
                                    "Expiration date must be tomorrow or later",
                                });
                              } else {
                                clearErrors("expiration_date");
                              }
                            }}
                            className="flex justify-center gap-2"
                          />
                        </PopoverContent>
                      </Popover>
                      <ErrorLabel
                        message={
                          errors["expiration_date"] &&
                          errors["expiration_date"].message
                        }
                      />
                    </div>
                  )}
                />
              )}

              <div className="flex flex-col gap-4">
                <Controller
                  name="num_of_vacancy"
                  control={control}
                  render={({ field }) => (
                    <div>
                      {<FormLabel label="No of Vacancy" />}
                      <Input
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          clearErrors("num_of_vacancy");
                        }}
                        min={0}
                        aria-invalid={
                          errors["num_of_vacancy"] &&
                          errors["num_of_vacancy"].message
                            ? true
                            : false
                        }
                        type={"number"}
                        placeholder={"Enter no of vancancy"}
                        error={
                          errors["num_of_vacancy"] &&
                          errors["num_of_vacancy"].message
                        }
                      />
                    </div>
                  )}
                />
                <Controller
                  name="apply_link"
                  control={control}
                  render={({ field }) => (
                    <div>
                      {<FormLabel label="Apply Link" />}
                      <PrefixInput
                        {...field}
                        aria-invalid={
                          errors["apply_link"] && errors["apply_link"].message
                            ? true
                            : false
                        }
                        value={field?.value}
                        onChange={field?.onChange}
                        placeholder={"youtube.com"}
                        type="text"
                        error={
                          errors["apply_link"] && errors["apply_link"].message
                        }
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        name: "header_code",
        label: "Header Code ",
        colSpan: 12,
        type: "textarea",
        placeholder: "Description Here",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full  w-full`,
        visable: true,
      },
      {
        name: "footer_code",
        label: "Footer Code ",
        colSpan: 12,
        type: "textarea",
        placeholder: "Description Here",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full  w-full`,
        visable: true,
      },
      {
        name: "meta_title",
        accordian_label: "Meta Title",
        label: "Meta Title",
        colSpan: 12,
        type: "textarea",
        placeholder: "Meta title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full  rounded-md w-full`,
        visable: true,
      },
      {
        name: "meta_description",
        accordian_label: "Meta Description",
        label: "Meta Description",
        colSpan: 12,
        type: "textarea",
        placeholder: "Meta Description",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full  rounded-md w-full`,
        visable: true,
      },
    ],
    [control, errors, setValue, isPending, watch("enable_auto_expiration")]
  );

  if (fetchPending && searchParams.get("page-item"))
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id="careers"
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan}  col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default CareerForm;
